import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSiteSettings } from '@/lib/siteSettings';
import { openInquiryModal } from '@/components/feature/InquiryModal';

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'About', href: '/about' },
  {
    label: 'Services',
    href: '#',
    children: [
      { label: 'Trading', href: '/trading' },
      { label: 'Procurement', href: '/procurement' },
      { label: 'Logistics', href: '/logistics' },
      { label: 'Freight Forwarding', href: '/freight-forwarding' },
      { label: 'Clearing & Forwarding', href: '/clearing-forwarding' },
    ],
  },
  { label: 'Products', href: '/products' },
  { label: 'Industries', href: '/industries' },
  { label: 'News', href: '/news' },
  { label: 'FAQ', href: '/faq' },
  { label: 'Contact', href: '/contact' },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState<string | null>(null);
  const [logoFailed, setLogoFailed] = useState(false);
  const location = useLocation();
  const { settings } = useSiteSettings();

  useEffect(() => {
    setMobileOpen(false);
    setDropdownOpen(null);
  }, [location.pathname]);

  const isActive = (href: string) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href) && href !== '/';
  };

  const handleGetQuote = () => {
    openInquiryModal();
  };

  return (
    <nav
      className="fixed top-0 left-0 w-full z-50 transition-all duration-500 bg-white border-b border-background-200/70"
    >
      <div className="w-full px-6 md:px-10 lg:px-16">
        <div className="flex items-center justify-between h-[72px] md:h-[84px]">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            {logoFailed ? (
              <span className="text-xl md:text-2xl font-heading font-bold uppercase tracking-tight transition-all duration-300 text-primary-700">
                BEKAALI
              </span>
            ) : (
              <img
                src={settings.logo_url}
                alt="BEKAALI ENTERPRISES LIMITED"
                onError={() => setLogoFailed(true)}
                className="h-12 md:h-14 w-auto object-contain transition-all duration-300 group-hover:scale-105"
              />
            )}
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => {
              if (link.children) {
                return (
                  <div
                    key={link.label}
                    className="relative"
                    onMouseEnter={() => setDropdownOpen(link.label)}
                    onMouseLeave={() => setDropdownOpen(null)}
                  >
                    <button
                      className="px-4 py-2 text-sm font-medium rounded-full transition-all duration-300 whitespace-nowrap flex items-center gap-1 cursor-pointer text-foreground-700 hover:text-primary-600"
                    >
                      {link.label}
                      <i className={`ri-arrow-down-s-line text-xs transition-transform duration-300 ${dropdownOpen === link.label ? 'rotate-180' : ''}`}></i>
                    </button>
                    {dropdownOpen === link.label && (
                      <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2">
                        <div className="bg-background-50 rounded-xl border border-background-200/70 py-2 px-1 min-w-[220px] shadow-lg">
                          {link.children.map((child) => (
                            <Link
                              key={child.href}
                              to={child.href}
                              className={`block px-4 py-2.5 text-sm rounded-lg transition-all duration-200 whitespace-nowrap ${
                                isActive(child.href)
                                  ? 'bg-primary-50 text-primary-700 font-medium'
                                  : 'text-foreground-700 hover:bg-background-100 hover:text-primary-600'
                              }`}
                            >
                              {child.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-300 whitespace-nowrap ${
                    isActive(link.href)
                      ? 'text-primary-600'
                      : 'text-foreground-700 hover:text-primary-600'
                  }`}
                >
                  {link.label}
                </Link>
              );
            })}
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <button
              onClick={handleGetQuote}
              className="px-5 py-2.5 text-sm font-semibold rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer bg-primary-500 text-white hover:bg-primary-600"
            >
              Get a Quote
            </button>
          </div>

          {/* Mobile Hamburger */}
          <button
            className={`lg:hidden flex items-center justify-center w-10 h-10 rounded-full transition-all duration-300 cursor-pointer text-foreground-800`}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-500 ${
          mobileOpen ? 'max-h-[800px]' : 'max-h-0'
        }`}
      >
        <div className="px-6 pb-6 pt-2 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
          {navLinks.map((link) => {
            if (link.children) {
              return (
                <div key={link.label} className="mb-1">
                  <button
                    className="w-full flex items-center justify-between py-3 text-sm font-medium cursor-pointer text-foreground-700"
                    onClick={() => setDropdownOpen(dropdownOpen === link.label ? null : link.label)}
                  >
                    {link.label}
                    <i className={`ri-arrow-down-s-line transition-transform duration-300 ${dropdownOpen === link.label ? 'rotate-180' : ''}`}></i>
                  </button>
                  {dropdownOpen === link.label && (
                    <div className="pl-4 py-1 space-y-0.5">
                      {link.children.map((child) => (
                        <Link
                          key={child.href}
                          to={child.href}
                          onClick={() => setMobileOpen(false)}
                          className={`block py-2.5 text-sm rounded-lg px-3 transition-all duration-200 ${
                            isActive(child.href)
                              ? 'bg-primary-50 text-primary-700 font-medium'
                              : 'text-foreground-600 hover:text-primary-600'
                          }`}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setMobileOpen(false)}
                className={`block py-3 text-sm font-medium transition-all duration-200 ${
                  isActive(link.href)
                    ? 'text-primary-600'
                    : 'text-foreground-700'
                }`}
              >
                {link.label}
              </Link>
            );
          })}
          <button
            onClick={() => { setMobileOpen(false); openInquiryModal(); }}
            className="block w-full mt-4 text-center px-5 py-3 text-sm font-semibold rounded-full bg-accent-500 text-background-50 hover:bg-accent-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            Get a Quote
          </button>
        </div>
      </div>
    </nav>
  );
}